"use client";

import { useTranslations } from "next-intl";
import { DATE_RANGES, DateRangeValue } from "@/lib/constants";
import { dateFilterChipClassName } from "./filter-chip-styles";

interface DateFilterProps {
  dateRange: DateRangeValue;
  onDateRangeChange: (range: DateRangeValue) => void;
  customStartDate: string | null;
  customEndDate: string | null;
  onCustomStartDateChange: (date: string | null) => void;
  onCustomEndDateChange: (date: string | null) => void;
  /** Values rendered after children, so extra filters can sit mid-row */
  trailingValues?: DateRangeValue[];
  /** Extra elements rendered inline between leading and trailing buttons */
  children?: React.ReactNode;
}

export function DateFilter({
  dateRange,
  onDateRangeChange,
  customStartDate,
  customEndDate,
  onCustomStartDateChange,
  onCustomEndDateChange,
  trailingValues = [],
  children,
}: DateFilterProps) {
  const t = useTranslations("Filters");

  const chip = ({ labelKey, value }: (typeof DATE_RANGES)[number]) => (
    <button
      key={value}
      onClick={() => onDateRangeChange(value)}
      className={dateFilterChipClassName(dateRange === value)}
    >
      {t(labelKey)}
    </button>
  );

  const customInputs = (
    <>
      <input
        type="date"
        value={customStartDate ?? ""}
        onChange={(e) => onCustomStartDateChange(e.target.value || null)}
        className="rounded-md border border-neutral-700 bg-transparent px-2.5 py-1.5 font-mono text-xs text-neutral-300 outline-none transition-colors focus:border-acid [&::-webkit-calendar-picker-indicator]:invert"
      />
      <span className="font-mono text-xs text-neutral-500">{t("to")}</span>
      <input
        type="date"
        value={customEndDate ?? ""}
        min={customStartDate ?? undefined}
        onChange={(e) => onCustomEndDateChange(e.target.value || null)}
        className="rounded-md border border-neutral-700 bg-transparent px-2.5 py-1.5 font-mono text-xs text-neutral-300 outline-none transition-colors focus:border-acid [&::-webkit-calendar-picker-indicator]:invert"
      />
    </>
  );

  return (
    <div>
      {/* Single scroll row on mobile, wrapping row on desktop */}
      <div className="flex items-center gap-2 max-sm:-mx-4 max-sm:overflow-x-auto max-sm:px-4 max-sm:no-scrollbar max-sm:scroll-fade-r sm:flex-wrap [&>*]:shrink-0 [&>*]:whitespace-nowrap">
        {DATE_RANGES.filter(({ value }) => !trailingValues.includes(value)).map(chip)}
        {children}
        {DATE_RANGES.filter(({ value }) => trailingValues.includes(value)).map(chip)}
        {dateRange === "custom" && (
          <div className="hidden sm:contents">{customInputs}</div>
        )}
      </div>
      {/* Custom range inputs get their own row on mobile so they aren't buried at the end of the scroller */}
      {dateRange === "custom" && (
        <div className="mt-2 flex items-center gap-2 sm:hidden">{customInputs}</div>
      )}
    </div>
  );
}
